import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Button } from '@/components/ui/Button'
import { Kbd } from '@/components/ui/Kbd'
import { cn } from '@/lib/cn'
import { formatShortcut } from '@/lib/format'
import { usePlatform } from '@/store/app-store'
import type { ShortcutState } from '@shared/types'

export interface ShortcutRecorderProps {
  value: string
  onChange: (accelerator: string) => void
  disabled?: boolean
  /** Forwarded to the button so a `<label htmlFor>` has something to point at. */
  id?: string
}

/** `event.code` values Electron names differently, or not at all as a code. */
const KEY_CODES: Record<string, string> = {
  Space: 'Space',
  Tab: 'Tab',
  Enter: 'Return',
  NumpadEnter: 'Return',
  Backspace: 'Backspace',
  Delete: 'Delete',
  Insert: 'Insert',
  Home: 'Home',
  End: 'End',
  PageUp: 'PageUp',
  PageDown: 'PageDown',
  ArrowUp: 'Up',
  ArrowDown: 'Down',
  ArrowLeft: 'Left',
  ArrowRight: 'Right',
  Minus: '-',
  Equal: '=',
  BracketLeft: '[',
  BracketRight: ']',
  Backslash: '\\',
  Semicolon: ';',
  Quote: "'",
  Comma: ',',
  Period: '.',
  Slash: '/',
  Backquote: '`',
}

/**
 * Accelerators name a physical key, and `event.key` stops being one the moment
 * Alt is held — Option+S is "ß" on macOS. `event.code` is the only field that
 * still says "the S key" under every modifier.
 *
 * Returns null for modifier-only presses and for keys Electron cannot express,
 * which the caller treats as "keep waiting".
 */
function acceleratorKey(code: string): string | null {
  if (/^Key[A-Z]$/.test(code)) return code.slice(3)
  if (/^Digit[0-9]$/.test(code)) return code.slice(5)
  if (/^Numpad[0-9]$/.test(code)) return `num${code.slice(6)}`
  if (/^F([1-9]|1[0-9]|2[0-4])$/.test(code)) return code
  return KEY_CODES[code] ?? null
}

function isFunctionKey(key: string): boolean {
  return /^F([1-9]|1[0-9]|2[0-4])$/.test(key)
}

/**
 * What Electron calls the key `event.metaKey` reports, per platform.
 *
 * `Command` exists only on macOS; the same physical key is `Super` on Linux and
 * the Windows key on Win32, where Electron spells it `Super` too. Emitting
 * `Command` off-mac would produce an accelerator the OS can never register.
 */
function metaModifier(os: string): string {
  return os === 'darwin' ? 'Command' : 'Super'
}

/**
 * The modifiers held during `event`, as Electron accelerator tokens.
 *
 * Command and Control are kept apart deliberately. `CommandOrControl` is the
 * right token for a *shipped default* that has to mean something sensible on
 * three platforms, but it is the wrong answer for a key the user physically
 * pressed: Electron resolves it to Command on macOS, so recording Control+N
 * bound Cmd+N and every Control-based combination was unreachable on a Mac.
 */
function modifiersOf(event: KeyboardEvent, os: string): string[] {
  const modifiers: string[] = []
  if (event.metaKey) modifiers.push(metaModifier(os))
  if (event.ctrlKey) modifiers.push('Control')
  if (event.altKey) modifiers.push('Alt')
  if (event.shiftKey) modifiers.push('Shift')
  return modifiers
}

/**
 * Whether the main process actually got the accelerator that is stored.
 *
 * Fetched once as well as subscribed: the first registration happens while the
 * panel is still loading, so its broadcast is delivered to nobody. Null in the
 * web build, which has no global accelerator to fail at.
 */
function useShortcutState(): ShortcutState | null {
  const [state, setState] = useState<ShortcutState | null>(null)

  useEffect(() => {
    const bridge = window.jot
    if (!bridge) return

    let live = true
    void bridge.shortcuts.get().then((next) => {
      // A broadcast can land before the fetch resolves; it is the fresher answer.
      if (live) setState((current) => current ?? next)
    })
    const unsubscribe = bridge.shortcuts.onChanged(setState)

    return () => {
      live = false
      unsubscribe()
    }
  }, [])

  return state
}

export function ShortcutRecorder({
  value,
  onChange,
  disabled,
  id,
}: ShortcutRecorderProps): ReactNode {
  const platform = usePlatform()
  const [recording, setRecording] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const os = platform.info.os
  const state = useShortcutState()

  /**
   * Callers pass an inline arrow, so `onChange` is a new function on every
   * render. Reading it from a ref keeps it out of the effect's dependencies:
   * otherwise an unrelated re-render mid-recording would tear the listener down
   * and back up, and the `resume()` in the cleanup would re-register the global
   * accelerator — which the OS then swallows before the keystroke the user is
   * trying to record can reach us. Suspending must last the whole session.
   */
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  useEffect(() => {
    if (!recording) return

    // A global accelerator is consumed by the OS before any listener here can
    // see it, so the combination that is currently bound — the likeliest thing
    // to press while re-recording — would toggle the panel away instead of
    // being captured. Stand it down for as long as we are listening.
    const bridge = window.jot
    bridge?.shortcuts.suspend()

    const onKeyDown = (event: KeyboardEvent): void => {
      // Capture phase, prevented and stopped: while recording, this component
      // owns the keyboard. Otherwise the panel's own Cmd+K / Escape bindings
      // would fire for the very combination the user is trying to assign.
      event.preventDefault()
      event.stopPropagation()

      // Escape always cancels, even with modifiers held. Reserving it costs one
      // exotic accelerator and guarantees a way out of recording mode.
      if (event.key === 'Escape') {
        setRecording(false)
        setError(null)
        return
      }

      const key = acceleratorKey(event.code)
      if (!key) return

      const modifiers = modifiersOf(event, os)

      // A bare letter as a global shortcut would swallow that key everywhere.
      if (modifiers.length === 0 && !isFunctionKey(key)) {
        setError('Needs a modifier')
        return
      }

      setRecording(false)
      setError(null)
      onChangeRef.current([...modifiers, key].join('+'))
    }

    // Losing the window mid-recording means the next keystroke lands somewhere
    // else entirely; stop listening rather than capture it on return.
    const onBlur = (): void => setRecording(false)

    window.addEventListener('keydown', onKeyDown, true)
    window.addEventListener('blur', onBlur)
    return () => {
      window.removeEventListener('keydown', onKeyDown, true)
      window.removeEventListener('blur', onBlur)
      bridge?.shortcuts.resume()
    }
  }, [recording, os])

  const formatted = formatShortcut(value, os)
  // format.ts joins keys with a thin space; \s matches it.
  const keys = formatted.split(/\s+/).filter((key) => key.length > 0)

  // Only speak about the value on screen: a save in flight briefly leaves the
  // main process reporting on the previous accelerator.
  const rejected = state !== null && !state.bound && state.accelerator === value
  const notice = !rejected
    ? null
    : state.fallback
      ? `Another app owns this. ${formatShortcut(state.fallback, os)} is active instead.`
      : 'Another app owns this. No global shortcut is active — use the tray icon.'

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        id={id}
        disabled={disabled}
        aria-pressed={recording}
        aria-label={
          recording
            ? 'Recording shortcut. Press a key combination, or Escape to cancel.'
            : `Global shortcut ${formatted}.${rejected ? ' Rejected by the system.' : ''} Activate to record a new one.`
        }
        onClick={() => {
          setError(null)
          setRecording((current) => !current)
        }}
        className={cn(
          'min-w-[124px]',
          recording && 'border-accent text-accent',
          // Not while recording: the button is then showing a prompt, not a value.
          !recording && rejected && 'border-danger',
        )}
      >
        {recording ? (
          'Press keys…'
        ) : keys.length > 0 ? (
          keys.map((key, index) => <Kbd key={`${key}-${index}`}>{key}</Kbd>)
        ) : (
          <span className="text-fg-faint">Not set</span>
        )}
      </Button>
      {/* The transient recording complaint wins: it is about the keystroke the
          user just made, while the notice describes the stored value. */}
      {(error ?? notice) ? (
        <span
          role="status"
          className="max-w-[210px] text-right text-[11px] leading-snug text-danger"
        >
          {error ?? notice}
        </span>
      ) : null}
    </div>
  )
}
